import PropTypes from 'prop-types';
import React from 'react';

import { updateCruiseStateroomClassPricing } from 'lib/utilities/updateCruiseStateroomClassPricing';
import sizes from 'styles/sizes';
import { CLOSED, MAX_CRUISES_AMMOUNT_DESKTOP, MAX_CRUISES_AMMOUNT_MOBILE, MAX_CRUISES_AMMOUNT_TABLET, SELECTING } from './constants';
export const CruiseSelectionContext = React.createContext({});

/**
 * Holds the state for effects from users interactions with cruise options
 */
class CruiseSelectionContextProvider extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			cruiseDetail: null,
			comparisonViewState: CLOSED, // selecting, closed
			cruisesComparing: [],
			maxCountForSelection: MAX_CRUISES_AMMOUNT_DESKTOP,
			remainingCountForSelection: MAX_CRUISES_AMMOUNT_DESKTOP,

			isMobile: process.browser && window?.innerWidth <= sizes.media.smallMaxWidth,
			isTablet: process.browser && window?.innerWidth <= sizes.media.tabletPortrait,
			isTabletLandscape: process.browser && window?.innerWidth <= sizes.media.mediumMaxWidth,
			isDesktop: process.browser && window?.innerWidth >= sizes.media.mediumMaxWidth,

			openCruiseDetail: this.openCruiseDetail,
			closeCruiseDetail: this.closeCruiseDetail,
			updateCruisesComparing: this.updateCruisesComparing,
			addCruiseToComparing: this.addCruiseToComparing,
			removeCruiseFromComparing: this.removeCruiseFromComparing,
			resetCruisesComparing: this.resetCruisesComparing,
			setComparisonViewClosed: this.setComparisonViewClosed,
			setComparisonViewSelecting: this.setComparisonViewSelecting,
			updateWatchlistCruiseDetail: this.updateWatchlistCruiseDetail,
		};
	}

	componentDidMount() {
		window.addEventListener('resize', this.onResize);
		this.updateMaxCountForSelection();
	}

	componentDidUpdate(_previousProps, previousState) {
		// check for changes on maxCountForSelection
		if (previousState.maxCountForSelection !== this.state.maxCountForSelection) {
			const remainingCount = this.state.maxCountForSelection - this.state.cruisesComparing.length;
			const cruisesComparing = [...this.state.cruisesComparing];

			if (cruisesComparing.length > this.state.maxCountForSelection) {
				cruisesComparing.splice(0, cruisesComparing.length - this.state.maxCountForSelection);
			}

			this.setState({
				remainingCountForSelection: remainingCount > 0 ? remainingCount : 0,
				cruisesComparing,
			});
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onResize);
	}

	updateMaxCountForSelection = () => {
		if (this.state.isMobile) {
			this.setState({ maxCountForSelection: MAX_CRUISES_AMMOUNT_MOBILE });
			return;
		}

		if (this.state.isTablet || this.state.isTabletLandscape) {
			this.setState({ maxCountForSelection: MAX_CRUISES_AMMOUNT_TABLET });
			return;
		}

		if (this.state.isDesktop) {
			this.setState({ maxCountForSelection: MAX_CRUISES_AMMOUNT_DESKTOP });
		}
	};

	onResize = () => {
		let maxCountForSelection;

		const isDesktop = window?.innerWidth >= sizes.media.mediumMaxWidth;
		if (isDesktop) {
			maxCountForSelection = MAX_CRUISES_AMMOUNT_DESKTOP;
		}

		const isTabletLandscape = window?.innerWidth <= sizes.media.mediumMaxWidth;
		if (isTabletLandscape) {
			maxCountForSelection = MAX_CRUISES_AMMOUNT_TABLET;
		}

		const isTablet = window?.innerWidth <= sizes.media.tabletPortrait;
		if (isTablet) {
			maxCountForSelection = MAX_CRUISES_AMMOUNT_TABLET;
		}

		const isMobile = window?.innerWidth <= sizes.media.smallMaxWidth;
		if (isMobile) {
			maxCountForSelection = MAX_CRUISES_AMMOUNT_MOBILE;
		}

		this.setState({
			isMobile,
			isTablet,
			isTabletLandscape,
			isDesktop,
			maxCountForSelection,
		});
	};

	openCruiseDetail = (cruiseDetail) => {
		this.setState({ cruiseDetail });
	};

	closeCruiseDetail = () => {
		this.setState({ cruiseDetail: null });
	};

	updateCruisesComparing = (cruisesComparing) => this.setState({ cruisesComparing });

	addCruiseToComparing = (cruise) => {
		if (!cruise || Object.keys(cruise).length === 0) return;
		this.setState((prevState) => {
			const cruisesComparing = Array.from(new Map([...prevState.cruisesComparing, cruise].map((cruise) => [cruise.id, cruise])).values());
			const remainingCount = this.state.maxCountForSelection - cruisesComparing.length;
			return {
				...prevState,
				cruisesComparing,
				remainingCountForSelection: remainingCount > 0 ? remainingCount : 0,
			};
		});
	};

	removeCruiseFromComparing = (cruise) => {
		if (!cruise || Object.keys(cruise).length === 0) return;
		this.setState((prevState) => {
			const cruisesComparing = prevState.cruisesComparing.filter((c) => c.id !== cruise.id);
			const remainingCount = this.state.maxCountForSelection - cruisesComparing.length;
			return {
				...prevState,
				cruisesComparing,
				remainingCountForSelection: remainingCount > 0 ? remainingCount : 0,
			};
		});
	};

	resetCruisesComparing = () => this.setState({ cruisesComparing: [], remainingCountForSelection: this.state.maxCountForSelection });

	setComparisonViewClosed = () => {
		this.setState({ comparisonViewState: CLOSED });
	};

	setComparisonViewSelecting = () => {
		this.setState({ comparisonViewState: SELECTING });
	};

	updateWatchlistCruiseDetail = (status, sailings) => {
		const { cruiseDetail } = this.state;
		if (!cruiseDetail) return;
		this.setState({
			cruiseDetail: updateCruiseStateroomClassPricing(cruiseDetail, status, sailings),
		});
	};

	render() {
		return <CruiseSelectionContext.Provider value={this.state}>{this.props.children}</CruiseSelectionContext.Provider>;
	}
}

CruiseSelectionContextProvider.propTypes = {
	children: PropTypes.node,
	language: PropTypes.string,
	market: PropTypes.string,
};

export default CruiseSelectionContextProvider;
